<template>
    <div class="overflow-x-auto border border-dark-gray-200 bg-white">
        <table class="w-full border-collapse text-sm" style="table-layout: fixed">
            <colgroup>
                <col style="width: 20rem" />
                <col style="width: 6rem" />
                <col v-for="column in columns" :key="`col-${column.key}`" style="width: 10rem" />
            </colgroup>

            <thead>
                <tr class="bg-dark-gray-200">
                    <th class="sticky left-0 z-20 border-b border-r border-dark-gray-200 bg-dark-gray-200 px-3 py-2 text-left font-semibold">
                        {{ $t('views.farmToFarm.table.labelHeader') }}
                    </th>
                    <th class="sticky z-20 border-b border-r border-dark-gray-200 bg-dark-gray-200 px-3 py-2 text-left font-semibold" style="left: 20rem">
                        {{ $t('views.farmToFarm.table.unitHeader') }}
                    </th>
                    <th v-for="column in columns" :key="`head-${column.key}`" class="border-b border-dark-gray-200 px-3 py-2 text-right font-semibold" :class="{ 'bg-white': column.isAggregate }">
                        {{ column.name }}
                    </th>
                </tr>
            </thead>

            <tbody>
                <template v-for="group in report.groups" :key="group.key">
                    <tr class="bg-dark-gray-100">
                        <td class="sticky left-0 z-10 border-b border-r border-dark-gray-200 bg-dark-gray-100 px-3 py-2 font-semibold text-dark-gray-800">
                            {{ group.header }}
                        </td>
                        <td class="sticky z-10 border-b border-r border-dark-gray-200 bg-dark-gray-100" style="left: 20rem"></td>
                        <td v-for="column in columns" :key="`grouphead-${group.key}-${column.key}`" class="border-b border-dark-gray-200"></td>
                    </tr>

                    <tr v-for="row in group.rows" :key="row.field_key" class="hover:bg-dark-gray-100">
                        <td class="sticky left-0 z-10 border-b border-r border-dark-gray-200 bg-white px-3 py-1.5 text-dark-gray-800">
                            {{ row.label }}
                        </td>
                        <td class="sticky z-10 border-b border-r border-dark-gray-200 bg-white px-3 py-1.5 text-dark-gray-600" style="left: 20rem">
                            {{ row.unit }}
                        </td>
                        <td v-for="column in columns" :key="`${row.field_key}-${column.key}`" class="border-b border-dark-gray-200 px-3 py-1.5 text-right tabular-nums" :class="cellClass(column)">
                            <TooltipProvider v-if="column.note">
                                <Tooltip>
                                    <TooltipTrigger as-child>
                                        <span>{{ formatCell(row, column) }}</span>
                                    </TooltipTrigger>
                                    <TooltipContent>{{ column.note }}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <span v-else>{{ formatCell(row, column) }}</span>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { farmToFarmColumns, FarmToFarmPeriodColumn } from '@/util/farmToFarmColumns';
import { FarmToFarmReport, FarmToFarmRow } from '@/types';

/**
 * The benchmark table for one period.
 *
 * Built as a real table with a fixed layout and sticky label and unit columns. CustomTable.vue
 * renders flexbox rows with JS-measured widths, which fights position: sticky - and there is no
 * frozen-column component in this codebase to reuse.
 */
export default defineComponent({
    components: { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger },
    props: {
        report: {
            type: Object as PropType<FarmToFarmReport>,
            required: true,
        },
        period: {
            type: String,
            required: true,
        },
        /** Header for the rollup column; the active directory's name. */
        aggregateLabel: {
            type: String,
            required: true,
        },
    },
    computed: {
        columns(): FarmToFarmPeriodColumn[] {
            return farmToFarmColumns(this.report, this.period, this.aggregateLabel);
        },
    },
    methods: {
        cellClass(column: FarmToFarmPeriodColumn): Record<string, boolean> {
            // Estimated figures are real numbers resting on a reconstructed status, so they
            // are shown but visibly marked rather than hidden.
            const estimated = column.state === 'estimated';
            // No figure at all. Muted the same way as `estimated` - the palette has a single
            // muted ink - so the two are told apart by italic vs. opacity, not by colour.
            const absent = column.state === 'unavailable' || column.state === 'noData';

            return {
                'bg-dark-gray-100': column.isAggregate,
                italic: estimated,
                'text-dark-gray-600': estimated || absent,
                'opacity-50': absent,
            };
        },
        formatCell(row: FarmToFarmRow, column: FarmToFarmPeriodColumn): string {
            const value = column.valueFor(row);

            // An em dash, never a zero. In a benchmark a zero reads as "this farm performed at
            // zero", which is a different and worse claim than "no figure".
            if (value === null) return '—';

            // Intl rather than $n: the decimal count varies per row, so none of the named
            // number formats fit. The i18n locale is already a bare tag (the _DK suffix is
            // stripped on load), so it can be handed to Intl directly.
            return new Intl.NumberFormat(this.$i18n.locale, {
                minimumFractionDigits: row.decimals,
                maximumFractionDigits: row.decimals,
            }).format(value);
        },
    },
});
</script>
